// i18n.js - Internationalization utility for UI labels

// Unicode character ranges for language detection
const hebrewChars = /[\u0590-\u05FF]/;
const arabicChars = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

/**
 * Translation dictionary for UI labels
 */
const translations = {
  en: {
    correct: "✓ Correct!",
    incorrect: "✗ Incorrect. The correct answer is:",
    tryAgain: "Try Again",
    submitAnswer: "Submit Answer",
    clickToExplore: "Click to explore",
    returnToQuiz: "Return to quiz",
    returnToText: "Return to text",
    backToTop: "Back to top",
    showMore: "... Show more",
    showLess: "Show less"
  },
  ar: {
    correct: "✓ صحيح!",
    incorrect: "✗ غير صحيح. الإجابة الصحيحة هي:",
    tryAgain: "حاول مرة أخرى",
    submitAnswer: "إرسال الإجابة",
    clickToExplore: "انقر للاستكشاف",
    returnToQuiz: "العودة إلى الاختبار",
    returnToText: "العودة إلى النص",
    backToTop: "العودة إلى الأعلى",
    showMore: "... عرض المزيد",
    showLess: "عرض أقل"
  },
  he: {
    correct: "✓ נכון!",
    incorrect: "✗ לא נכון. התשובה הנכונה היא:",
    tryAgain: "נסה שוב",
    submitAnswer: "שלח תשובה",
    clickToExplore: "לחץ לחקור",
    returnToQuiz: "חזור לחידון",
    returnToText: "חזור לטקסט",
    backToTop: "חזור למעלה",
    showMore: "... הצג עוד",
    showLess: "הצג פחות"
  }
};

/**
 * Detects the language of a text string based on character patterns
 * @param {string} text - The text to analyze
 * @param {number} threshold - Percentage threshold for language detection (default: 10)
 * @returns {string} Language code ('en', 'ar', 'he', etc.)
 */
export const detectLanguage = (text, threshold = 10) => {
  if (!text || typeof text !== 'string') return 'en';
  
  // Remove spaces, punctuation, and numbers for better analysis
  const cleanedText = text.replace(/[\s.,'"!?0-9]+/g, '');
  if (cleanedText.length === 0) return 'en';
  
  let hebrewCount = 0;
  let arabicCount = 0;
  
  for (let char of cleanedText) {
    if (hebrewChars.test(char)) hebrewCount++;
    if (arabicChars.test(char)) arabicCount++;
  }
  
  const hebrewPercent = (hebrewCount / cleanedText.length) * 100;
  const arabicPercent = (arabicCount / cleanedText.length) * 100;
  
  // Return the language if it exceeds threshold
  if (hebrewPercent > threshold) return 'he';
  if (arabicPercent > threshold) return 'ar';
  
  // Add more languages as needed:
  // Spanish, French, Chinese, etc.
  
  return 'en'; // Default to English
};

/**
 * Gets a translated UI label for the detected language
 * @param {string} key - Translation key (e.g., 'correct', 'tryAgain')
 * @param {string} language - Language code ('en', 'ar', 'he')
 * @returns {string} Translated text
 */
export const getTranslation = (key, language = 'en') => {
  return translations[language]?.[key] || translations.en[key];
};

/**
 * Gets translations object for a specific language
 * @param {string} language - Language code
 * @returns {object} All translations for that language
 */
export const getTranslations = (language = 'en') => {
  return translations[language] || translations.en;
};
